import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Chip,
  Fade,
  Zoom,
  Slide,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  WhatsApp,
  ShoppingCart,
  Storefront,
  RocketLaunch,
  ArrowForward,
  CheckCircle,
  Timeline,
  Payment,
  Speed,
  Security,
  Phone,
  Email,
  LocationOn,
  History,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createContact } from "../redux/slices/contactSlice";
import Header from "./Header";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #25D366 30%, #128C7E 90%)",
  border: 0,
  borderRadius: 25,
  boxShadow: "0 3px 15px rgba(37, 211, 102, 0.3)",
  color: "white",
  padding: "12px 30px",
  transition: "all 0.3s",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 20px rgba(37, 211, 102, 0.4)",
  },
}));

const ModernContactPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const planId = queryParams.get("planId");
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    business: "",
    city: "",
    planId: planId || null,
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [copied, setCopied] = useState("");

  const features = [
    {
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      title: "24/7 Ordering",
      desc: "Place orders anytime, anywhere with our mobile app",
      color: "#25D366",
    },
    {
      icon: <Timeline sx={{ fontSize: 40 }} />,
      title: "Real-time Tracking",
      desc: "Complete visibility of your orders and inventory",
      color: "#128C7E",
    },
    {
      icon: <RocketLaunch sx={{ fontSize: 40 }} />,
      title: "Fast Processing",
      desc: "Instant order confirmation and faster payments",
      color: "#075E54",
    },
    // {
    //   icon: <Inventory sx={{ fontSize: 40 }} />,
    //   title: 'Stock Management',
    //   desc: 'Real-time inventory updates and low stock alerts',
    //   color: '#25D366'
    // },
    {
      icon: <History sx={{ fontSize: 40 }} />,
      title: "Invoice History",
      desc: "Access all past invoices anytime, anywhere",
      color: "#25D366",
    },
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: "Secure Payments",
      desc: "Multiple payment options with instant settlement",
      color: "#128C7E",
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Fast Delivery",
      desc: "Priority delivery for app orders",
      color: "#075E54",
    },
  ];

  // const stats = [
  //   { value: '5000+', label: 'Active Dealers', icon: <Storefront /> },
  //   { value: '₹50Cr+', label: 'Monthly Orders', icon: <ShoppingCart /> },
  //   { value: '24/7', label: 'Support Available', icon: <Headset /> },
  //   { value: '98%', label: 'Dealer Satisfaction', icon: <Star /> }
  // ];

  const contactDetails = [
    { icon: <Phone />, text: "+91 8302044892", label: "Call Us" },
    {
      icon: <Email />,
      text: "bizbridgetechnologypvt@gmail.com",
      label: "Email",
    },
    { icon: <LocationOn />, text: "Malviya Nagar, Jaipur", label: "Visit" },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      dispatch(createContact(formData));
      setSnackbar({
        open: true,
        message: "Thank you! Our team will contact you within 30 minutes.",
        severity: "success",
      });
      setFormData({ name: "", phone: "", business: "", city: "" });
    }, 2000);
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          bgcolor: "#0A1929",
          minHeight: "100vh",
          color: "white",
          position: "relative",
          overflow: "hidden",
          // mt:7
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "-5%",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(37,211,102,0.1) 0%, rgba(10,25,41,0) 70%)",
            animation: "pulse 4s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 0.5 },
              "50%": { transform: "scale(1.2)", opacity: 0.8 },
            },
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "-5%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(18,140,126,0.1) 0%, rgba(10,25,41,0) 70%)",
            animation: "pulse 5s ease-in-out infinite",
          }}
        />

        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0A1929 0%, #1A3B5C 100%)",
            pt: { xs: 8, md: 12 },
            pb: { xs: 10, md: 16 },
            mt: 10,
            position: "relative",
            borderBottom: "1px solid rgba(37, 211, 102, 0.2)",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Zoom in timeout={1000}>
                  <Box>
                    {/* <Grow in timeout={800}>
                    <Chip
                      label="DEALER APP 2.0 LAUNCHED"
                      sx={{
                        bgcolor: 'rgba(37, 211, 102, 0.1)',
                        color: '#25D366',
                        fontWeight: 700,
                        mb: 3,
                        border: '1px solid #25D366',
                        fontSize: '0.9rem',
                        py: 2.5
                      }}
                    />
                  </Grow> */}
                    <Slide direction="right" in timeout={1000}>
                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: { xs: "2.8rem", md: "4.2rem" },
                          fontWeight: 800,
                          lineHeight: 1.2,
                          mb: 3,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {/* Stop WhatsApp{" "}  */}
                        Stop Manual {" "} 
                        <Box
                          component="span"
                          sx={{
                            color: "#25D366",
                            position: "relative",
                            display: "inline-block",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              bottom: 5,
                              left: 0,
                              width: "100%",
                              height: "8px",
                              bgcolor: "rgba(37, 211, 102, 0.3)",
                              borderRadius: "4px",
                            },
                          }}
                        >
                           Chaos
                          {/* Order 24/7 */}
                        </Box>
                        {" "} Start ERP Automation.
                      </Typography>
                    </Slide>

                    <Fade in timeout={1500}>
                      <Typography
                        variant="h5"
                        sx={{
                          color: "#B2B9C5",
                          mb: 4,
                          lineHeight: 1.7,
                          fontSize: { xs: "1.1rem", md: "1.3rem" },
                        }}
                      >
                        {/* Take your company online and empower your dealers with a
                        powerful ordering app. Stop the WhatsApp chaos. Get
                        instant orders, faster payments, and complete visibility
                        into your entire dealer network — 24/7. */}
                        Take your business online and empower your team with a powerful ERP platform. Stop the spreadsheet and email chaos. Get automated workflows, real-time financial control, and complete visibility into your entire operations — 24/7.
                      </Typography>
                    </Fade>

                    <Fade in timeout={2000}>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {/* <GradientButton
                        size="small"
                       
                        endIcon={<ArrowForward />}
                        onClick={() => window.scrollTo({ top: document.getElementById('dealer-form').offsetTop - 100, behavior: 'smooth' })}
                      >
                        Become a Seller Today
                        
                      </GradientButton> */}
                        <GradientButton
                          size="small"
                          endIcon={<ArrowForward />}
                          onClick={() => navigate("/register")}
                        >
                          Unlock Your Digital Growth
                        </GradientButton>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<WhatsApp />}
                          sx={{
                            borderColor: "#25D366",
                            color: "#25D366",
                            py: 1.5,
                            px: 4,
                            fontSize: "1.1rem",
                            borderRadius: "25px",
                            "&:hover": {
                              borderColor: "#128C7E",
                              color: "#128C7E",
                              bgcolor: "rgba(37, 211, 102, 0.1)",
                            },
                          }}
                          href="https://wa.me/918005968738?text=Hello%20BizBridge!%20I%20am%20interested%20in%20becoming%20a%20seller.%20Please%20contact%20me."
                          target="_blank"
                        >
                          WhatsApp Us
                        </Button>
                      </Box>
                    </Fade>
                  </Box>
                </Zoom>
              </Grid>

              {/* Form Section */}
              <Grid item xs={12} md={6} id="dealer-form">
                <Fade in timeout={1500}>
                  <Paper
                    elevation={24}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.05)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 6,
                      p: { xs: 3, md: 5 },
                      border: "1px solid rgba(37, 211, 102, 0.2)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                    }}
                  >
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: "#25D366",
                          margin: "0 auto 20px",
                          boxShadow: "0 10px 20px rgba(37, 211, 102, 0.3)",
                        }}
                      >
                        <Storefront sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, mb: 1, color: "white" }}
                      >
                        {/* Join as Dealer */}
                        {/* Transform Your Dealer Network Digitally */}
                        Transform Your Business Operations Digitally
                      </Typography>
                      <Typography sx={{ color: "#B2B9C5" }}>
                        {/* Get app download link instantly on WhatsApp */}
                        Fill this quick form and our team will contact you
                        within 30 minutes
                      </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="name"
                            label="Full Name"
                            variant="outlined"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                color: "white",
                                borderRadius: "15px",
                                "& fieldset": {
                                  borderColor: "rgba(37, 211, 102, 0.3)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#25D366",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#25D366",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: "#B2B9C5",
                                "&.Mui-focused": {
                                  color: "#25D366",
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="phone"
                            label="WhatsApp Number"
                            variant="outlined"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                color: "white",
                                borderRadius: "15px",
                                "& fieldset": {
                                  borderColor: "rgba(37, 211, 102, 0.3)",
                                },
                                "&:hover fieldset": { borderColor: "#25D366" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#25D366",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: "#B2B9C5",
                                "&.Mui-focused": { color: "#25D366" },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="business"
                            label="Business Name"
                            variant="outlined"
                            value={formData.business}
                            onChange={handleChange}
                            required
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                color: "white",
                                borderRadius: "15px",
                                "& fieldset": {
                                  borderColor: "rgba(37, 211, 102, 0.3)",
                                },
                                "&:hover fieldset": { borderColor: "#25D366" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#25D366",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: "#B2B9C5",
                                "&.Mui-focused": { color: "#25D366" },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="city"
                            label="City"
                            variant="outlined"
                            value={formData.city}
                            onChange={handleChange}
                            required
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                color: "white",
                                borderRadius: "15px",
                                "& fieldset": {
                                  borderColor: "rgba(37, 211, 102, 0.3)",
                                },
                                "&:hover fieldset": { borderColor: "#25D366" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#25D366",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: "#B2B9C5",
                                "&.Mui-focused": { color: "#25D366" },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                              background:
                                "linear-gradient(45deg, #25D366, #128C7E)",
                              py: 2,
                              borderRadius: "15px",
                              fontSize: "1.2rem",
                              fontWeight: 600,
                              "&:hover": {
                                background:
                                  "linear-gradient(45deg, #128C7E, #075E54)",
                              },
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              // "Launch Your Own Dealer App" 
                              "Request ERP Demo"
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Trust Badges */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        mt: 3,
                      }}
                    >
                      <Chip
                        icon={<Security />}
                        label="Secure"
                        size="small"
                        sx={{
                          color: "#B2B9C5",
                          borderColor: "rgba(37,211,102,0.3)",
                        }}
                        variant="outlined"
                      />
                      <Chip
                        icon={<CheckCircle />}
                        label="Verified"
                        size="small"
                        sx={{
                          color: "#B2B9C5",
                          borderColor: "rgba(37,211,102,0.3)",
                        }}
                        variant="outlined"
                      />
                      <Chip
                        icon={<Speed />}
                        label="Instant"
                        size="small"
                        sx={{
                          color: "#B2B9C5",
                          borderColor: "rgba(37,211,102,0.3)",
                        }}
                        variant="outlined"
                      />
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
      <Footer />
    </>
  );
};

export default ModernContactPage;
